import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Music as MusicIcon, ExternalLink, Sparkles, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MusicPage() {
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/extras/music`)
      .then((res) => setPlaylists(res.data))
      .catch(() => setPlaylists([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="music-back-btn"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              data-testid="music-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center gap-3"
              aria-label="Explore care types"
            >
              <img
                src="/tyvena-lockup-horizontal.png"
                alt="Tyvena"
                className="h-10 md:h-11 w-auto rounded-lg shadow-md"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full mb-6">
          <Sparkles className="w-4 h-4 text-secondary" />
          <span className="text-sm font-body font-semibold text-text-primary">
            Curated by Tyvena
          </span>
        </div>
        <div className="flex items-start gap-4 md:gap-6">
          <div className="hidden md:flex flex-shrink-0 w-16 h-16 rounded-2xl bg-primary/10 items-center justify-center">
            <MusicIcon className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary leading-tight mb-4">
              Music for the Journey
            </h1>
            <p className="text-lg text-text-secondary font-body max-w-2xl leading-relaxed">
              Music reaches places words can't. Whether it's a Sinatra tune that lights up a
              parent with dementia or a hymn that carries a family through a hard evening —
              these playlists were chosen with intention. Play them on your phone, a bedside
              speaker, or during a visit.
            </p>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : playlists.length === 0 ? (
          <p className="text-center text-text-secondary font-body py-16">No playlists to show right now.</p>
        ) : (
          <div data-testid="music-playlist-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {playlists.map((p) => (
              <a
                key={p.id}
                data-testid={`playlist-${p.id}`}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl border border-[#E2E8F0] shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col"
              >
                <div className="relative h-40 overflow-hidden">
                  {p.cover_url && (
                    <img
                      src={p.cover_url}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="absolute inset-0 opacity-40 mix-blend-multiply" style={{ background: p.accent_color || "#2AADA0" }}></div>
                  {p.provider && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/95 text-[11px] font-body font-bold text-text-primary uppercase tracking-wider shadow-sm">
                      {p.provider}
                    </div>
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  {p.theme && (
                    <p className="text-xs font-body font-bold uppercase tracking-wider text-primary mb-2">
                      {p.theme}
                    </p>
                  )}
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-2 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-sm text-text-secondary font-body leading-relaxed mb-4 flex-1">
                    {p.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm font-body font-semibold text-primary group-hover:gap-3 transition-all">
                    Open playlist
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        <p className="text-xs text-text-secondary font-body text-center mt-10 max-w-2xl mx-auto">
          Playlists open in Spotify or YouTube — free accounts work. Tyvena earns nothing from
          these links; they're here because we think they'll help.
        </p>
      </section>
    </div>
  );
}
